import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Tip } from "grommet";
import {
  Add,
  PersonalComputer,
  User,
  Edit,
  FormDown,
  Lock,
  MailOption,
  Notification,
  Phone,
  Search,
  StatusCritical,
  Trash,
} from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { AddUser } from "./";
import { UserService, AuthService } from "../../services";
import { useSetCurrentUser } from "./redux/hooks";
import { presentAlertPrompt } from "@shared/Alert";
import { formatPhoneNumber } from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import StaffNotificationSchedule from "./StaffNotificationSchedule";
import { useFetchHotel } from "./redux/hooks";
import { avatarColor, initials } from "./avatar";
import {
  STAFF_DEPARTMENTS,
  normalizeStaffDepartment,
  staffDepartmentLabel,
} from "./staffDepartments";
import "./SettingsUsers.scss";
import { hasMultipleGroupProperties } from "./staffRoles";

const roleLabel = (role, isGroupedHotel, t) => {
  const key =
    role === "user-multi" ? "userMulti" : role === "manager" ? "manager" : role;
  if (!isGroupedHotel && ["admin", "user"].includes(role)) {
    return t(`addUser.roles.${key}.standaloneLabel`);
  }
  return t(`addUser.roles.${key}.label`, role);
};

export default function SettingsUsers() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [activeTab, setActiveTab] = useState("team");
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const { currentUser, isLoxeAdmin } = useSetCurrentUser(null);
  const { hotel } = useFetchHotel();
  const [openEdit, setOpenEdit] = useState(false);
  const [userId, setUserId] = useState(0);

  const onRefresh = () => {
    UserService.getUsers().then(
      (response) => setList(response.data),
      presentToastErrorContent,
    );
  };

  useEffect(() => {
    onRefresh();
  }, []);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return list.filter((user) => {
      const matchesDepartment =
        department === "all" ||
        normalizeStaffDepartment(user.department) === department;
      const haystack =
        `${user.username || ""} ${user.email || ""} ${user.role || ""} ${staffDepartmentLabel(user.department, t)}`.toLowerCase();
      return matchesDepartment && (!query || haystack.includes(query));
    });
  }, [department, list, search, t]);

  const scheduledWithoutPush = useMemo(
    () =>
      list.filter(
        (user) =>
          (user.notifEmail || user.notifText) &&
          !(user.notifPush && user.mobilePushIds?.length > 0),
      ),
    [list],
  );

  const handleEditModalClose = (refresh) => {
    if (refresh) onRefresh();
    setOpenEdit(false);
  };

  const handleRemoveUser = (id) => {
    presentAlertPrompt({
      title: t("settingsUsers.deleteConfirmTitle"),
      message: t("settingsUsers.deleteConfirmMessage"),
      onOK: () =>
        UserService.deleteUser(id).then(() => {
          onRefresh();
          presentToastSuccess("User successfully removed.");
        }, presentToastErrorContent),
      buttonOKText: t("settingsUsers.deleteConfirmButton"),
      buttonOKColor: "status-critical",
    });
  };

  const handleResetPassword = (id) => {
    presentAlertPrompt({
      title: t("settingsUsers.resetConfirmTitle"),
      message: t("settingsUsers.resetConfirmMessage"),
      onOK: () =>
        AuthService.resetPassword(id).then(
          () => presentToastSuccess("Password reset."),
          presentToastErrorContent,
        ),
      buttonOKText: t("settingsUsers.resetConfirmButton"),
      buttonOKColor: "status-critical",
    });
  };

  return (
    <section className="dashboard-settings-users">
      <header className="staff-page__header">
        <div>
          <h1>{t("settingsUsers.title")}</h1>
          <p>{t("settingsUsers.subtitle")}</p>
        </div>
        <Button
          primary
          icon={<Add size="small" />}
          label={t("settingsUsers.addUser")}
          onClick={() => {
            setUserId(0);
            setOpenEdit(true);
          }}
        />
      </header>

      <nav className="staff-tabs" aria-label="Staff settings">
        <button
          className={activeTab === "team" ? "is-active" : ""}
          onClick={() => setActiveTab("team")}
        >
          {t("settingsUsers.teamMembers")}
        </button>
        <button
          className={activeTab === "schedule" ? "is-active" : ""}
          onClick={() => setActiveTab("schedule")}
        >
          {t("settingsUsers.notificationSchedule")}
        </button>
      </nav>

      {scheduledWithoutPush.length > 0 && (
        <section className="staff-push-warning" role="alert">
          <span className="staff-push-warning__icon">
            <StatusCritical size="22px" />
          </span>
          <div>
            <strong>
              {t("settingsUsers.pushWarningTitle", {
                context:
                  scheduledWithoutPush.length === 1 ? "singular" : "plural",
                count: scheduledWithoutPush.length,
              })}
            </strong>
            <p>
              {t("settingsUsers.pushWarningDetail", {
                names: scheduledWithoutPush
                  .map((user) => user.username || user.email)
                  .join(", "),
              })}
            </p>
          </div>
        </section>
      )}

      {activeTab === "team" ? (
        <>
          <div className="staff-filters">
            <label className="staff-search">
              <Search size="18px" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("settingsUsers.searchPlaceholder")}
              />
            </label>
            <label className="staff-department-filter">
              <select
                value={department}
                onChange={(event) => setDepartment(event.target.value)}
              >
                <option value="all">{t("settingsUsers.allDepartments")}</option>
                {STAFF_DEPARTMENTS.map((item) => (
                  <option key={item} value={item}>{staffDepartmentLabel(item, t)}</option>
                ))}
              </select>
              <FormDown size="18px" aria-hidden="true" />
            </label>
          </div>
          <div className="staff-table-wrap panel-surface">
            <table className="staff-table">
              <thead>
                <tr>
                  <th>{t("settingsUsers.colName")}</th>
                  <th>{t("settingsUsers.colEmail")}</th>
                  <th>{t("settingsUsers.colRole")}</th>
                  <th>{t("settingsUsers.colDepartment")}</th>
                  <th>{t("settingsUsers.colPhone")}</th>
                  <th>{t("settingsUsers.colNotifications")}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="staff-person">
                        <span
                          className="staff-avatar"
                          style={{
                            backgroundColor: avatarColor(
                              user.username || user.email,
                            ),
                          }}
                        >
                          {initials(user.username || user.email)}
                        </span>
                        <div className="staff-identity">
                          <strong>{user.username}</strong>
                          <span className="staff-account-type">
                            {user.accountType === "shared-workstation" && <PersonalComputer size="14px" aria-hidden="true" />}
                            {user.accountType === "individual" && <User size="14px" aria-hidden="true" />}
                            {t(`addUser.accountTypes.${user.accountType || "unclassified"}`)}
                          </span>
                        </div>
                        {String(user.id) === String(currentUser?.id) && (
                          <em>You</em>
                        )}
                      </div>
                    </td>
                    <td>{user.email || "-"}</td>
                    <td>
                      <span className={`role-pill role-pill--${user.role}`}>
                        {roleLabel(user.role, hasMultipleGroupProperties(hotel, isLoxeAdmin), t)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`department-pill department-pill--${normalizeStaffDepartment(user.department).toLowerCase()}`}
                      >
                        {staffDepartmentLabel(user.department, t)}
                      </span>
                    </td>
                    <td>{user.phone ? formatPhoneNumber(user.phone) : "-"}</td>
                    <td>
                      <div className="notification-icons">
                        {user.notifEmail && (
                          <span title="Email">
                            <MailOption size="16px" />
                          </span>
                        )}
                        {user.notifText && user.phone && (
                          <span title="Text">
                            <Phone size="16px" />
                          </span>
                        )}
                        <Tip
                          content={t(
                            user.notifPush
                              ? user.mobilePushIds?.length > 0
                                ? "settingsUsers.pushReady"
                                : "settingsUsers.pushNotEnabled"
                              : "settingsUsers.pushDisabled",
                          )}
                        >
                          <span
                            className={`push-status ${
                              user.notifPush && user.mobilePushIds?.length > 0
                                ? "is-ready"
                                : "has-warning"
                            }`}
                          >
                            {user.notifPush &&
                            user.mobilePushIds?.length > 0 ? (
                              <Notification size="16px" />
                            ) : (
                              <strong aria-hidden="true">!</strong>
                            )}
                          </span>
                        </Tip>
                      </div>
                    </td>
                    <td>
                      <Box
                        direction="row"
                        gap="medium"
                        className="staff-actions"
                      >
                        <Button
                          hoverIndicator="accent-4"
                          icon={<Edit size="small" />}
                          size="small"
                          primary
                          color="status-warning"
                          title="Edit"
                          onClick={() => {
                            setUserId(user.id);
                            setOpenEdit(true);
                          }}
                        />
                        {(currentUser?.role !== "user" ||
                          user.id === currentUser?.id) && (
                          <Button
                            hoverIndicator="dark-1"
                            icon={<Lock size="small" />}
                            size="small"
                            primary
                            color="neutral-3"
                            title="Reset password"
                            onClick={() => handleResetPassword(user.id)}
                          />
                        )}
                        <Button
                          hoverIndicator="dark-1"
                          icon={<Trash size="small" />}
                          size="small"
                          primary
                          color="status-critical"
                          title="Delete"
                          disabled={
                            user.id === currentUser?.id ||
                            currentUser?.role === "user"
                          }
                          onClick={() => handleRemoveUser(user.id)}
                        />
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="staff-empty">{t("settingsUsers.noMatches")}</div>
            )}
          </div>
          <div className="staff-info">
            <Notification size="19px" />
            <div>
              <strong>{t("settingsUsers.aboutNotifications")}</strong>
              <p>{t("settingsUsers.aboutNotificationsText")}</p>
            </div>
          </div>
        </>
      ) : (
        <StaffNotificationSchedule users={list} onRefresh={onRefresh} />
      )}

      {openEdit && (
        <AddUser
          userId={userId}
          currentUser={currentUser}
          hotel={hotel}
          onClose={handleEditModalClose}
          onRefresh={onRefresh}
          isLoxeAdmin={isLoxeAdmin}
        />
      )}
    </section>
  );
}
